import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../config/apiRouting';
import './scheduling-components.css';

interface Employee {
  employeeId: string;
  firstName: string;
  lastName: string;
  position: string;
  department: string;
  phoneNumber: string;
}

interface AvailabilitySlot {
  slotId: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  isAvailable: boolean;
  notes?: string;
}

interface EmployeeAvailability {
  availabilityId: string;
  employeeId: string;
  employee: Employee;
  availability: AvailabilitySlot[];
  maxHoursPerWeek: number;
  minHoursPerWeek: number;
  preferredHours: number;
  canWorkWeekends: boolean;
  canWorkHolidays: boolean;
  updatedAt: string;
}

interface EmployeeAvailabilityProps {
  storeId: string;
  enterpriseId: string;
}

export const EmployeeAvailability: React.FC<EmployeeAvailabilityProps> = ({ 
  storeId, 
  enterpriseId 
}) => {
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [availabilities, setAvailabilities] = useState<EmployeeAvailability[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<string>('');
  const [editingAvailability, setEditingAvailability] = useState<EmployeeAvailability | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  
  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  
  const defaultAvailability = daysOfWeek.map((_, index) => ({
    slotId: `slot-${index}`,
    dayOfWeek: index,
    startTime: '09:00',
    endTime: '17:00',
    isAvailable: index >= 1 && index <= 5, // M-F by default
    notes: ''
  }));

  useEffect(() => {
    fetchEmployees();
    fetchAvailabilities();
  }, [storeId, enterpriseId]);

  const fetchEmployees = async () => {
    try {
      const response = await fetch(
        `${API_ENDPOINTS.employees(enterpriseId)}&storeId=${storeId}`
      );
      if (response.ok) {
        const data = await response.json();
        setEmployees(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchAvailabilities = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${API_ENDPOINTS.employees(enterpriseId)}/availability?storeId=${storeId}`
      );
      if (response.ok) {
        const data = await response.json();
        setAvailabilities(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching availabilities:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditAvailability = (employeeId: string) => {
    const existingAvailability = availabilities.find(a => a.employeeId === employeeId);
    const employee = employees.find(e => e.employeeId === employeeId);
    
    if (existingAvailability) {
      setEditingAvailability(existingAvailability);
    } else if (employee) {
      // Create new availability record
      setEditingAvailability({
        availabilityId: '',
        employeeId: employeeId,
        employee: employee,
        availability: defaultAvailability,
        maxHoursPerWeek: 40,
        minHoursPerWeek: 20,
        preferredHours: 32,
        canWorkWeekends: true,
        canWorkHolidays: false,
        updatedAt: new Date().toISOString()
      });
    }
    setShowEditModal(true);
  };

  const handleSaveAvailability = async () => {
    if (!editingAvailability) return;

    try {
      const method = editingAvailability.availabilityId ? 'PUT' : 'POST';
      const url = editingAvailability.availabilityId 
        ? `${API_ENDPOINTS.employees(enterpriseId)}/availability/${editingAvailability.availabilityId}`
        : `${API_ENDPOINTS.employees(enterpriseId)}/availability`;

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...editingAvailability,
          storeId,
          enterpriseId
        })
      });

      if (response.ok) {
        setShowEditModal(false);
        setEditingAvailability(null);
        fetchAvailabilities();
      }
    } catch (error) {
      console.error('Error saving availability:', error);
    }
  };

  const updateSlotAvailability = (slotId: string, updates: Partial<AvailabilitySlot>) => {
    if (!editingAvailability) return;

    setEditingAvailability({
      ...editingAvailability,
      availability: editingAvailability.availability.map(slot =>
        slot.slotId === slotId ? { ...slot, ...updates } : slot
      )
    });
  };


  const getAvailabilitySummary = (availability: AvailabilitySlot[]) => {
    const availableDays = availability.filter(slot => slot.isAvailable);
    if (availableDays.length === 0) return 'Not Available';
    if (availableDays.length === 7) return 'Available All Days';
    
    const dayRanges: string[] = [];
    let rangeStart = -1;
    let rangeEnd = -1;
    
    for (let i = 0; i < 7; i++) {
      if (availability[i].isAvailable) {
        if (rangeStart === -1) rangeStart = i;
        rangeEnd = i;
      } else if (rangeStart !== -1) {
        if (rangeStart === rangeEnd) {
          dayRanges.push(daysOfWeek[rangeStart].slice(0, 3));
        } else {
          dayRanges.push(`${daysOfWeek[rangeStart].slice(0, 3)}-${daysOfWeek[rangeEnd].slice(0, 3)}`);
        }
        rangeStart = -1;
      }
    }
    
    if (rangeStart !== -1) {
      if (rangeStart === rangeEnd) {
        dayRanges.push(daysOfWeek[rangeStart].slice(0, 3));
      } else {
        dayRanges.push(`${daysOfWeek[rangeStart].slice(0, 3)}-${daysOfWeek[rangeEnd].slice(0, 3)}`);
      }
    }
    
    return dayRanges.join(', ');
  };

  if (loading) {
    return <div className="loading">Loading employee availability...</div>;
  }

  return (
    <div className="employee-availability">
      <div className="page-header">
        <h2>Employee Availability</h2>
        <div className="header-actions">
          <select 
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
            className="employee-filter"
          >
            <option value="">All Employees</option>
            {employees.map(emp => (
              <option key={emp.employeeId} value={emp.employeeId}>
                {emp.firstName} {emp.lastName}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="availability-grid">
        {employees
          .filter(emp => !selectedEmployee || emp.employeeId === selectedEmployee)
          .map(employee => {
            const availability = availabilities.find(a => a.employeeId === employee.employeeId);
            
            return (
              <div key={employee.employeeId} className="availability-card">
                <div className="employee-info">
                  <h3>{employee.firstName} {employee.lastName}</h3>
                  <p className="position">{employee.position}</p>
                  <p className="department">{employee.department}</p>
                </div>
                
                <div className="availability-summary">
                  {availability ? (
                    <>
                      <div className="summary-item">
                        <span className="label">Available:</span>
                        <span className="value">{getAvailabilitySummary(availability.availability)}</span>
                      </div>
                      <div className="summary-item">
                        <span className="label">Preferred Hours:</span>
                        <span className="value">{availability.preferredHours} hrs/week</span>
                      </div>
                      <div className="summary-item">
                        <span className="label">Hour Range:</span>
                        <span className="value">{availability.minHoursPerWeek}-{availability.maxHoursPerWeek} hrs</span>
                      </div>
                      <div className="summary-flags">
                        {availability.canWorkWeekends && <span className="flag weekends">Weekends OK</span>}
                        {availability.canWorkHolidays && <span className="flag holidays">Holidays OK</span>}
                      </div>
                    </>
                  ) : (
                    <p className="no-availability">No availability set</p>
                  )}
                </div>
                
                <div className="card-actions">
                  <button 
                    className="btn btn-secondary"
                    onClick={() => handleEditAvailability(employee.employeeId)}
                  >
                    {availability ? 'Edit Availability' : 'Set Availability'}
                  </button>
                </div>
              </div>
            );
          })}
      </div>

      {showEditModal && editingAvailability && (
        <div className="modal-overlay">
          <div className="modal large">
            <div className="modal-header">
              <h3>Edit Availability - {editingAvailability.employee.firstName} {editingAvailability.employee.lastName}</h3>
              <button onClick={() => {
                setShowEditModal(false);
                setEditingAvailability(null);
              }}>×</button>
            </div>
            <div className="modal-content">
              <div className="availability-form">
                <div className="weekly-availability">
                  <h4>Weekly Availability</h4>
                  <div className="availability-slots">
                    {editingAvailability.availability.map((slot) => (
                      <div key={slot.slotId} className="availability-slot">
                        <div className="slot-day">
                          <label>
                            <input
                              type="checkbox"
                              checked={slot.isAvailable}
                              onChange={(e) => updateSlotAvailability(slot.slotId, { isAvailable: e.target.checked })}
                            />
                            {daysOfWeek[slot.dayOfWeek]}
                          </label>
                        </div>
                        {slot.isAvailable && (
                          <>
                            <div className="slot-times">
                              <input
                                type="time"
                                value={slot.startTime}
                                onChange={(e) => updateSlotAvailability(slot.slotId, { startTime: e.target.value })}
                              />
                              <span>to</span>
                              <input
                                type="time"
                                value={slot.endTime}
                                onChange={(e) => updateSlotAvailability(slot.slotId, { endTime: e.target.value })}
                              />
                            </div>
                            <input
                              type="text"
                              className="slot-notes"
                              placeholder="Notes (optional)"
                              value={slot.notes || ''}
                              onChange={(e) => updateSlotAvailability(slot.slotId, { notes: e.target.value })}
                            />
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="hour-preferences">
                  <h4>Hour Preferences</h4>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Minimum Hours/Week</label>
                      <input
                        type="number"
                        min="0"
                        max="168"
                        value={editingAvailability.minHoursPerWeek}
                        onChange={(e) => setEditingAvailability({
                          ...editingAvailability,
                          minHoursPerWeek: parseInt(e.target.value) || 0
                        })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Maximum Hours/Week</label>
                      <input
                        type="number"
                        min="0"
                        max="168"
                        value={editingAvailability.maxHoursPerWeek}
                        onChange={(e) => setEditingAvailability({
                          ...editingAvailability,
                          maxHoursPerWeek: parseInt(e.target.value) || 0
                        })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Preferred Hours/Week</label>
                      <input
                        type="number"
                        min="0"
                        max="168"
                        value={editingAvailability.preferredHours}
                        onChange={(e) => setEditingAvailability({
                          ...editingAvailability,
                          preferredHours: parseInt(e.target.value) || 0
                        })}
                      />
                    </div>
                  </div>
                </div>

                <div className="special-availability">
                  <h4>Special Availability</h4>
                  <div className="checkbox-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={editingAvailability.canWorkWeekends}
                        onChange={(e) => setEditingAvailability({
                          ...editingAvailability,
                          canWorkWeekends: e.target.checked
                        })}
                      />
                      Can work weekends
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={editingAvailability.canWorkHolidays}
                        onChange={(e) => setEditingAvailability({
                          ...editingAvailability,
                          canWorkHolidays: e.target.checked
                        })}
                      />
                      Can work holidays
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button 
                className="btn btn-secondary" 
                onClick={() => {
                  setShowEditModal(false);
                  setEditingAvailability(null);
                }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleSaveAvailability}
              >
                Save Availability
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};